import { default as React, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { CloudAccountType } from './types/CloudAccountTypes';
import { GetCloudAccountsAndRolesForApplication } from './cloudAccounts/CloudAccountQueries';
import * as XLSX from 'xlsx';
import { CNAPCGAAudit, Device } from './types/InsightsTypes';
import {
  applicationCloudAccountQueryUrl,
  applicationDeviceQueryUrl,
  GetDevicesQuery,
} from './queries/InsightsQueries';
import {
  asExportableCnapData,
  asExportableDevicesForAudit,
  escapeCSVValue,
} from '../../util/export';
import { fetchCnapData } from '../../util/cnap';

import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  DevHubButton,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubMenuItem,
  DevHubSelect,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

interface BulkExportResourcesReportProps {
  appIds: number[];
  handleAlert: (alert: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }) => void;
}

enum FILETYPE {
  CSV = 'csv',
  EXCEL = 'excel',
}

interface CloudAccountExportType {
  'Account name': string;
  'Account ID': string;
  Platform: string;
  Environment: string;
  'Technical owner': string;
  'Associated applications': string;
}

interface AppResourceData {
  appId: number;
  appName: string;
  devices: Device[];
  cloudAccounts: CloudAccountExportType[];
  cnapData: CNAPCGAAudit[];
  owner: string;
}

export enum DeviceType {
  ChassisServer = 'CHASSIS_SERVER',
  Daas = 'DAAS',
  Database = 'DATABASE',
  FddiNetwork = 'FDDI_NETWORK',
  Msd = 'MSD',
  Netgear = 'NETGEAR',
  PhysicalServer = 'PHYSICAL_SERVER',
  Unknown = 'UNKNOWN',
  VirtualMachine = 'VIRTUAL_MACHINE',
}

/**
 * Component for exporting bulk application reports for multiple apps.
 * @param BulkExportResourcesReportProps props - The properties for the component.
 */
const BulkExportResourcesReport = ({
  appIds,
  handleAlert,
}: BulkExportResourcesReportProps) => {
  const config = useApi(configApiRef);
  const cnapCGAAuditUrl = config.getString('app.cnapCGAAuditUrl');

  const [resourceDownloading, setResourceDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<boolean>(false);

  const [format, setFormat] = useState('excel');

  const [fetchCloudAccounts] = useLazyQuery(
    GetCloudAccountsAndRolesForApplication,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const [fetchDevices, { fetchMore }] = useLazyQuery(GetDevicesQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const fetchAppResources = async (appId: number): Promise<AppResourceData> => {
    // CNAP data will be fetched via helper; initialize when fetched
    let cnapData: CNAPCGAAudit[];

    const cloudResult = await fetchCloudAccounts({
      variables: { applicationId: appId },
    });
    const accounts: CloudAccountType[] =
      cloudResult.data?.resources?.application?.cloudAccounts || [];

    const formattedCloudData = accounts.map(cloudAccount => ({
      'Account name': cloudAccount.name,
      'Account ID': cloudAccount.id,
      Platform: cloudAccount.platform,
      Environment: cloudAccount.environment,
      'Technical owner':
        cloudResult.data?.resources?.application?.owner.displayName ?? '',
      'Associated applications':
        cloudResult.data?.resources?.application?.name ?? '',
    }));

    const deviceResult = await fetchDevices({
      variables: {
        applicationId: appId,
        pagination: { limit: 100, offset: 0 },
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    });

    const devicesPaged =
      deviceResult.data?.resources?.application?.devicesPaged;
    const totalCount = devicesPaged?.totalCount ?? 0;
    let allDevices = devicesPaged?.devices ?? [];

    if (totalCount > 100) {
      const offsets = [];
      for (let i = 100; i < totalCount; i += 100) {
        offsets.push(i);
      }

      try {
        const results = await Promise.all(
          offsets.map(offset =>
            fetchMore({
              variables: { pagination: { limit: 100, offset } },
              updateQuery: (prev, { fetchMoreResult }) =>
                fetchMoreResult || prev,
            }),
          ),
        );

        const moreDevices = results.flatMap(
          moreData =>
            moreData.data?.resources?.application?.devicesPaged?.devices ?? [],
        );

        allDevices = [...allDevices, ...moreDevices];
      } catch (err) {
        // Error fetching devices - silently continue
      }
    }

    // CNAP fetch via shared helper
    try {
      cnapData = await fetchCnapData(config, appId);
    } catch {
      cnapData = [];
    }

    return {
      appId,
      appName: cloudResult.data?.resources?.application?.name || `App ${appId}`,
      devices: allDevices,
      cloudAccounts: formattedCloudData,
      cnapData,
      owner: cloudResult.data?.resources?.application?.owner.displayName || '',
    };
  };

  const handleBulkCSVDownload = async (appsData: AppResourceData[]) => {
    const csvData: any[] = [['Bulk Compliance Report'], []];

    appsData.forEach(appData => {
      const physicalServers = appData.devices.filter(
        d => d.type === DeviceType.PhysicalServer,
      );
      const virtualMachines = appData.devices.filter(
        d => d.type === DeviceType.VirtualMachine,
      );

      csvData.push([`Application: ${appData.appName} (ID: ${appData.appId})`]);
      csvData.push([`Owner: ${appData.owner}`]);
      csvData.push([]);

      csvData.push(['OnPrem Servers']);
      if (physicalServers.length > 0) {
        const exportableDevices = asExportableDevicesForAudit(physicalServers);
        const headers = Object.keys(exportableDevices[0]);
        csvData.push(headers);
        exportableDevices.forEach(device => {
          csvData.push(headers.map(header => escapeCSVValue(device[header])));
        });
      } else {
        csvData.push(['', 'no data available']);
      }
      csvData.push([]);

      csvData.push(['Private Cloud Servers']);
      if (virtualMachines.length > 0) {
        const exportableDevices = asExportableDevicesForAudit(virtualMachines);
        const headers = Object.keys(exportableDevices[0]);
        csvData.push(headers);
        exportableDevices.forEach(device => {
          csvData.push(headers.map(header => escapeCSVValue(device[header])));
        });
      } else {
        csvData.push(['', 'no data available']);
      }
      csvData.push([]);

      csvData.push(['Cloud Accounts']);
      if (appData.cloudAccounts.length > 0) {
        csvData.push(['Account name', 'Account ID', 'Platform', 'Environment']);
        appData.cloudAccounts.forEach(account => {
          csvData.push([
            account['Account name'],
            account['Account ID'],
            account.Platform,
            account.Environment,
          ]);
        });
      } else {
        csvData.push(['', 'no data available']);
      }
      csvData.push([]);

      csvData.push(['CNAP Report']);
      if (appData.cnapData.length > 0) {
        csvData.push([
          'DevHub App Name',
          'Application ID',
          'App Route',
          'App Name',
          'Subnet',
          'Platform',
        ]);
        appData.cnapData.forEach(cnap => {
          csvData.push([
            cnap.devhubAppName,
            cnap.applicationId,
            cnap.appRoute,
            cnap.appName,
            cnap.subnet,
            cnap.platform,
          ]);
        });
      } else {
        csvData.push(['', 'no data available']);
      }
      csvData.push([]);
      csvData.push([]);
    });

    csvData.push(['Links to fetch data']);
    csvData.push(['Query name', 'Url']);
    csvData.push(['Cloud Accounts query', applicationCloudAccountQueryUrl]);
    csvData.push(['Devices query', applicationDeviceQueryUrl]);
    csvData.push(['CNAP Report API', cnapCGAAuditUrl]);

    const csvContent = `data:text/csv;charset=utf-8,${csvData
      .map(e => e.join(','))
      .join('\n')}`;
    const encodedUri = encodeURI(csvContent);

    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `Bulk_Compliance_Report_${appIds.join('_')}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkExportExcel = async (appsData: AppResourceData[]) => {
    const workbook = XLSX.utils.book_new();

    workbook.Props = {
      Title: `Compliance Report for ${appIds.length} Application(s)`,
      Subject: 'Compliance Report',
    };

    const allPhysicalServers: Device[] = [];
    const allVirtualMachines: Device[] = [];
    const allCloudAccounts: any[] = [];
    const allCnapData: CNAPCGAAudit[] = [];

    appsData.forEach(appData => {
      const physicalServers = appData.devices.filter(
        d => d.type === DeviceType.PhysicalServer,
      );
      const virtualMachines = appData.devices.filter(
        d => d.type === DeviceType.VirtualMachine,
      );

      allPhysicalServers.push(...physicalServers);
      allVirtualMachines.push(...virtualMachines);
      allCloudAccounts.push(...appData.cloudAccounts);
      allCnapData.push(...appData.cnapData);
    });

    const addDeviceSheet = (devices: Device[], sheetName: string) => {
      const data =
        devices && devices.length > 0
          ? asExportableDevicesForAudit(devices)
          : [{ [sheetName]: 'No data available' }];
      const columnCount = data.length > 0 ? Object.keys(data[0]).length : 1;
      const worksheet = XLSX.utils.json_to_sheet(data);
      worksheet['!cols'] = Array(columnCount).fill({ wch: 20 });
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.utils.sheet_add_aoa(
        worksheet,
        [['Devices query', applicationDeviceQueryUrl]],
        { origin: data.length + 2 },
      );
    };

    const addSheetWithQuery = (
      data: any[],
      sheetName: string,
      queryLabel: string,
      queryUrl: string,
      exportFn?: (data: any[]) => any[],
    ) => {
      const exportData = exportFn ? exportFn(data) : data;
      const hasData = exportData.length > 0;
      const sheetData = hasData
        ? exportData
        : [{ [sheetName]: 'No data available' }];
      const columnCount =
        sheetData.length > 0 ? Object.keys(sheetData[0]).length : 1;
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      worksheet['!cols'] = Array(columnCount).fill({ wch: 20 });
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.utils.sheet_add_aoa(worksheet, [[queryLabel, queryUrl]], {
        origin: sheetData.length + 2,
      });
    };

    addDeviceSheet(allPhysicalServers, 'OnPrem Server');
    addDeviceSheet(allVirtualMachines, 'Private Cloud Server');

    addSheetWithQuery(
      allCloudAccounts,
      'Cloud Accounts',
      'Cloud Accounts query',
      applicationCloudAccountQueryUrl,
    );

    addSheetWithQuery(
      allCnapData,
      'CNAP Report',
      'CNAP Report API',
      cnapCGAAuditUrl,
      asExportableCnapData,
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const excelBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const excelUrl = URL.createObjectURL(excelBlob);
    const a = document.createElement('a');
    a.href = excelUrl;
    a.download = `Compliance_Report_${appIds.length}_Apps.xlsx`;
    a.click();
    URL.revokeObjectURL(excelUrl);
  };

  const handleBulkExport = async (exportType: FILETYPE) => {
    if (!appIds || appIds.length === 0) {
      handleAlert({
        open: true,
        message: 'Please select at least one application to export.',
        severity: 'error',
      });
      return;
    }

    setResourceDownloading(true);
    setDownloadError(false);

    handleAlert({
      open: true,
      message: `Exporting data for ${appIds.length} application(s). Please wait...`,
      severity: 'info',
    });

    try {
      const allAppsData = await Promise.all(
        appIds.map(appId => fetchAppResources(appId)),
      );

      if (exportType === FILETYPE.EXCEL) {
        await handleBulkExportExcel(allAppsData);
      } else {
        await handleBulkCSVDownload(allAppsData);
      }

      handleAlert({
        open: true,
        message: 'Export completed successfully.',
        severity: 'success',
      });
    } catch (error) {
      setDownloadError(true);
      handleAlert({
        open: true,
        message: 'Error occurred while exporting. Please try again.',
        severity: 'error',
      });
    } finally {
      setResourceDownloading(false);
    }
  };

  const handleClick = async (typeOfFile: string) => {
    const exportType = typeOfFile as FILETYPE;
    await handleBulkExport(exportType);
  };

  return (
    <React.Fragment>
      {resourceDownloading ? (
        <DevHubCircularProgress />
      ) : (
        <React.Fragment>
          {downloadError ? (
            <DevHubTooltip title="Something went wrong. Please try again after refreshing the page.">
              <span>
                <DevHubDynamicIcon
                  style={{ marginLeft: '0.5rem' }}
                  iconName="ReportProblem"
                  color="error"
                  fontSize="large"
                />
              </span>
            </DevHubTooltip>
          ) : (
            <DevHubGrid
              container
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <DevHubGrid item>
                <DevHubTypography variant="body1">Format</DevHubTypography>
              </DevHubGrid>
              <DevHubGrid item>
                <DevHubSelect
                  value={format}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFormat(e.target.value as string);
                  }}
                  disabled={!appIds || appIds.length === 0}
                >
                  <DevHubMenuItem value="csv">CSV</DevHubMenuItem>
                  <DevHubMenuItem value="excel">Excel</DevHubMenuItem>
                </DevHubSelect>
              </DevHubGrid>
              <DevHubGrid item>
                <DevHubButton
                  variant="contained"
                  color="primary"
                  onClick={() => handleClick(format)}
                  disabled={!appIds || appIds.length === 0}
                  data-gtm-event="export-bulk-resource-report"
                >
                  Export ({appIds?.length || 0} selected)
                </DevHubButton>
              </DevHubGrid>
            </DevHubGrid>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default BulkExportResourcesReport;
